import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import {
  UploadCloud as UploadCloudIcon,
  Trash2,
  AlertTriangle as AlertTriangleIcon,
  AlertCircle,
  FileText,
  X,
} from "lucide-react";
import YmdDateInput from "../ui/YmdDateInput";
import { apiService } from "../../services/apiService";
import { normalizeBillingAccount } from "../../utils/helpers";
import { formatGlCodeLabel, glCodesForDropdown } from "../../utils/glCodes";
import { isFutureInvoiceMonth, getCurrentInvoiceMonthEndDate } from "../../utils/invoiceMonthDisplay";
import InvoiceExtractionPdfPreviewModal from "./InvoiceExtractionPdfPreviewModal";

import CustomSelect from "../ui/CustomSelect";
const fieldClass =
  "w-full min-w-[7rem] border border-stone-200 rounded-lg px-2.5 py-1.5 text-sm text-stone-900 bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400";

const reviewStatusClass = {
  required: "border-red-200 bg-red-50/70 focus:ring-red-100 focus:border-red-400",
  optional: "border-amber-200 bg-amber-50/70 focus:ring-amber-100 focus:border-amber-400",
  check: "border-sky-200 bg-sky-50/70 focus:ring-sky-100 focus:border-sky-400",
};

function parseReviewAmount(value) {
  if (value === "" || value == null) return null;
  const n = parseFloat(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

function isValidYmd(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  const [y, m, d] = String(value).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === m - 1 &&
    dt.getUTCDate() === d
  );
}

/** Per-field review hints for empty extraction / arithmetic checks (visual only). */
function getReviewFieldStatuses(row) {
  const statuses = {};

  if (!String(row.provider || "").trim()) {
    statuses.provider = { level: "required", message: "Required" };
  }
  if (!String(row.invoice_number || "").trim()) {
    statuses.invoice_number = { level: "required", message: "Required" };
  }

  const invoiceDate = String(row.invoice_date || "").trim();
  if (!invoiceDate) {
    statuses.invoice_date = { level: "required", message: "Required" };
  } else if (!isValidYmd(invoiceDate)) {
    statuses.invoice_date = { level: "required", message: "Use YYYY-MM-DD" };
  }

  if (!String(row.billing_account || "").trim()) {
    statuses.billing_account = { level: "required", message: "Required" };
  }
  if (!String(row.gl_code || "").trim()) {
    statuses.gl_code = { level: "required", message: "Required" };
  }

  const mrc = parseReviewAmount(row.monthly_mrc);
  if (mrc === null) {
    statuses.monthly_mrc = { level: "required", message: "Missing" };
  }

  const total = parseReviewAmount(row.total_invoice_amount);
  if (total === null) {
    statuses.total_invoice_amount = { level: "required", message: "Missing" };
  } else if (total === 0) {
    statuses.total_invoice_amount = { level: "required", message: "reads 0" };
  }

  const taxesRaw = String(row.taxes_and_surcharges ?? "").trim();
  if (!taxesRaw) {
    statuses.taxes_and_surcharges = { level: "warning", message: "Missing" };
  } else if (parseReviewAmount(row.taxes_and_surcharges) === null) {
    statuses.taxes_and_surcharges = { level: "warning", message: "Invalid number" };
  }

  const dueRaw = String(row.due_date ?? "").trim();
  if (!dueRaw) {
    statuses.due_date = { level: "warning", message: "Missing" };
  } else if (!isValidYmd(dueRaw)) {
    statuses.due_date = { level: "warning", message: "Use YYYY-MM-DD" };
  }

  if (
    mrc !== null &&
    total !== null &&
    total !== 0 &&
    !statuses.total_invoice_amount
  ) {
    const taxes = taxesRaw ? parseReviewAmount(row.taxes_and_surcharges) ?? 0 : 0;
    const sum = mrc + taxes;
    const diff = Math.abs(total - sum);
    if (diff >= 0.01) {
      statuses.total_invoice_amount = {
        level: "check",
        message: `MRC plus taxes is ${sum.toFixed(2)}, total is ${total.toFixed(2)}. Difference ${diff.toFixed(2)} may be one time charges.`,
      };
    }
  }

  return statuses;
}

const reviewFieldClass = (status) => {
  const base =
    "h-11 w-full rounded-lg border px-3 pr-8 text-sm outline-none transition-colors placeholder:text-gray-400";

  if (!status) {
    return `${base} border-gray-200 bg-white text-gray-900 focus:border-gray-300 focus:ring-0`;
  }

  switch (status.level) {
    case "required":
      return `${base} border-red-300 bg-red-50 text-gray-900 focus:border-red-400 focus:ring-0`;

    case "warning":
      return `${base} border-amber-300 bg-amber-50 text-gray-900 focus:border-amber-400 focus:ring-0`;

    case "info":
      return `${base} border-gray-200 bg-white text-gray-900 focus:border-gray-300 focus:ring-0`;

    case "check":
      return `${base} border-sky-300 bg-sky-50 text-gray-900 focus:border-sky-400 focus:ring-0`;

    default:
      return `${base} border-gray-200 bg-white text-gray-900 focus:border-gray-300 focus:ring-0`;
  }
};

function reviewStatusIconClass(level) {
  if (level === "required") return "text-red-400";
  if (level === "optional") return "text-amber-400";
  if (level === "check") return "text-sky-400";
  return "";
}

function PdfFileIcon({ className = "h-10 w-10" }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      <path
        d="M7 2.5h13l6 6v20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-25a1 1 0 0 1 1-1z"
        fill="#F8FAFC"
        stroke="#CBD5E1"
        strokeWidth="1"
      />
      <path d="M20 2.5v5a1 1 0 0 0 1 1h5" fill="#EEF2F7" stroke="#CBD5E1" strokeWidth="1" />
      <rect x="5.2" y="19" width="16.5" height="9" rx="2" fill="#E11D2F" />
      <text
        x="13.45"
        y="25.4"
        textAnchor="middle"
        fontSize="7.2"
        fontWeight="700"
        fontFamily="Arial, Helvetica, sans-serif"
        fill="#ffffff"
        letterSpacing="0.3"
      >
        PDF
      </text>
    </svg>
  );
}

const ReviewFieldHint = ({ status }) => {
  if (!status) return null;

  if (status.level === "required") {
    return (
      <span
        className="pointer-events-none absolute -right-1.5 -top-1.5 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm"
        title={status.message}
      >
        !
      </span>
    );
  }

  if (status.level === "warning") {
    return (
      <span
        className="pointer-events-none absolute -right-1.5 -top-1.5 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white shadow-sm"
        title={status.message}
      >
        !
      </span>
    );
  }

  if (status.level === "check") {
    return (
      <span
        className="pointer-events-none absolute -right-1.5 -top-1.5 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-sky-500 text-[10px] font-bold text-white shadow-sm"
        title={status.message}
      >
        ?
      </span>
    );
  }

  return null;
};

const emptyRowFields = () => ({
  provider: "",
  invoice_number: "",
  invoice_date: "",
  billing_account: "",
  gl_code: "",
  monthly_mrc: "",
  taxes_and_surcharges: "",
  total_invoice_amount: "",
  due_date: "",
});

function normalizeProviderKey(value) {
  if (value == null) return "";
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function stripLegalSuffixes(normalized) {
  if (!normalized) return "";
  return normalized
    .replace(
      /\b(pty ltd|pty limited|limited|incorporated|inc|llc|ltd|corp|corporation|company|co|aus|australia|plc)\b/g,
      " ",
    )
    .replace(/\s+/g, " ")
    .trim();
}

/** Match extracted legal name to a known tenant provider (prefer shortest). */
function matchKnownProvider(extracted, knownProviders) {
  const list = Array.isArray(knownProviders)
    ? [...new Set(knownProviders.map((p) => String(p || "").trim()).filter(Boolean))]
    : [];
  if (!list.length) return null;
  const raw = extracted == null ? "" : String(extracted).trim();
  if (!raw) return null;
  const extractedNorm = normalizeProviderKey(raw);
  const extractedCore = stripLegalSuffixes(extractedNorm);
  if (!extractedNorm) return null;

  const hits = [];
  for (const known of list) {
    const knownNorm = normalizeProviderKey(known);
    if (!knownNorm || knownNorm.length < 2) continue;
    const knownCore = stripLegalSuffixes(knownNorm);
    const match =
      extractedNorm === knownNorm ||
      (extractedCore && knownCore && extractedCore === knownCore) ||
      extractedNorm.startsWith(`${knownNorm} `) ||
      (extractedCore && extractedCore.startsWith(`${knownNorm} `)) ||
      (extractedCore && knownCore && extractedCore.startsWith(`${knownCore} `)) ||
      (extractedCore && knownNorm.startsWith(`${extractedCore} `)) ||
      (extractedCore && knownCore && knownCore.startsWith(`${extractedCore} `));
    if (match) hits.push(known);
  }
  if (!hits.length) return null;
  hits.sort((a, b) => {
    const la = normalizeProviderKey(a).length;
    const lb = normalizeProviderKey(b).length;
    if (la !== lb) return la - lb;
    return a.localeCompare(b);
  });
  return hits[0];
}

function matchBillingAccount(extractedAccount, accountsForProvider) {
  const target = normalizeBillingAccount(extractedAccount);
  if (!target || !Array.isArray(accountsForProvider)) return "";
  const hit = accountsForProvider.find(
    (acc) => normalizeBillingAccount(acc) === target,
  );
  return hit != null ? String(hit) : "";
}

function fieldsFromExtracted(extracted, knownProviders, billingAccountsByProvider) {
  const base = emptyRowFields();
  if (!extracted || typeof extracted !== "object") return base;

  const rawProvider =
    extracted.provider != null ? String(extracted.provider).trim() : "";
  const matchedProvider = matchKnownProvider(rawProvider, knownProviders) || "";

  const accountsForProvider = [
    ...new Set(
      (billingAccountsByProvider || [])
        .filter((b) => b.provider === matchedProvider)
        .map((b) => normalizeBillingAccount(b.billing_account))
        .filter(Boolean),
    ),
  ];

  const rawAccount =
    extracted.billing_account != null ? String(extracted.billing_account).trim() : "";
  const normalizedRawAccount = normalizeBillingAccount(rawAccount);
  const matchedAccount = matchBillingAccount(rawAccount, accountsForProvider);

  let notConfiguredMessage = "";
  if (rawProvider && !matchedProvider && normalizedRawAccount) {
    notConfiguredMessage = `Extracted provider ${rawProvider} and account ${normalizedRawAccount} is not configured.`;
  } else if (rawProvider && !matchedProvider) {
    notConfiguredMessage = `Extracted provider ${rawProvider} is not configured.`;
  } else if (matchedProvider && normalizedRawAccount && !matchedAccount) {
    notConfiguredMessage = `Extracted provider and account ${normalizedRawAccount} is not configured.`;
  } else if (!matchedProvider && normalizedRawAccount) {
    notConfiguredMessage = `Extracted provider and account ${normalizedRawAccount} is not configured.`;
  }

  return {
    provider: matchedProvider,
    invoice_number:
      extracted.invoice_number != null ? String(extracted.invoice_number) : "",
    invoice_date:
      extracted.invoice_date != null
        ? String(extracted.invoice_date).slice(0, 10)
        : "",
    billing_account: matchedAccount,
    gl_code: "",
    monthly_mrc:
      extracted.monthly_mrc != null && extracted.monthly_mrc !== ""
        ? String(extracted.monthly_mrc)
        : "",
    taxes_and_surcharges:
      extracted.taxes_and_surcharges != null &&
      extracted.taxes_and_surcharges !== ""
        ? String(extracted.taxes_and_surcharges)
        : "",
    total_invoice_amount:
      extracted.total_invoice_amount != null &&
      extracted.total_invoice_amount !== ""
        ? String(extracted.total_invoice_amount)
        : "",
    due_date:
      extracted.due_date != null ? String(extracted.due_date).slice(0, 10) : "",
    extracted_provider: rawProvider,
    extracted_billing_account: normalizedRawAccount || rawAccount,
    notConfiguredMessage,
  };
}

const EMPTY_EXTRACTION_MESSAGE =
  "No invoice data could be extracted from this PDF. All summary fields were empty.";

/** Mandatory review fields before save. Returns short issue labels (e.g. Missing Monthly MRC). */
function validateReviewRow(row) {
  const errors = [];
  if (!String(row.provider || "").trim()) {
    errors.push("Missing Provider");
  }
  if (!String(row.invoice_number || "").trim()) {
    errors.push("Missing Invoice Number");
  }
  const invoiceDate = String(row.invoice_date || "").trim();
  if (!invoiceDate) {
    errors.push("Missing Invoice Date");
  } else if (!isValidYmd(invoiceDate)) {
    errors.push("Invoice Date must be YYYY-MM-DD");
  } else if (isFutureInvoiceMonth(invoiceDate)) {
    errors.push("Invoice Date cannot be in a future month");
  }
  if (!String(row.billing_account || "").trim()) {
    errors.push("Missing Billing Account");
  }
  if (!String(row.gl_code || "").trim()) {
    errors.push("Missing GL Code");
  }
  if (
    row.monthly_mrc === "" ||
    row.monthly_mrc == null ||
    Number.isNaN(parseFloat(row.monthly_mrc))
  ) {
    errors.push("Missing Monthly MRC");
  }
  if (
    row.total_invoice_amount === "" ||
    row.total_invoice_amount == null ||
    Number.isNaN(parseFloat(row.total_invoice_amount))
  ) {
    errors.push("Missing Total Invoice Amount");
  }
  if (
    row.taxes_and_surcharges != null &&
    String(row.taxes_and_surcharges).trim() !== "" &&
    Number.isNaN(parseFloat(row.taxes_and_surcharges))
  ) {
    errors.push("Taxes and Surcharges must be a valid number");
  }
  if (
    row.due_date != null &&
    String(row.due_date).trim() !== "" &&
    !isValidYmd(String(row.due_date).trim())
  ) {
    errors.push("Due Date must be YYYY-MM-DD");
  }
  return errors;
}

/** e.g. Lumen — Billing Account 5-6T2QZSCM — Missing Monthly MRC */
function formatValidationLine(row, issue) {
  const provider = String(row.provider || "").trim() || "Unknown provider";
  const account = String(row.billing_account || "").trim() || "—";
  return `${provider} — Billing Account ${account} — ${issue}`;
}

function hasAnyExtractedField(extracted) {
  if (!extracted || typeof extracted !== "object") return false;
  const keys = [
    "provider",
    "invoice_number",
    "invoice_date",
    "billing_account",
    "monthly_mrc",
    "taxes_and_surcharges",
    "total_invoice_amount",
    "due_date",
  ];
  return keys.some((key) => {
    const v = extracted[key];
    if (v == null) return false;
    if (typeof v === "string") return v.trim().length > 0;
    if (typeof v === "number") return Number.isFinite(v);
    return String(v).trim().length > 0;
  });
}

/**
 * Multi-PDF extract → review → save flow for monthly invoices.
 * Manual entry remains on the parent page.
 */
const InvoiceExtractionUpload = ({
  onBack,
  onSwitchToManual,
  onSaved,
  setToast,
  providers = [],
  billingAccountsByProvider = [],
  glCodes = [],
  canCloseAccount = false,
  refreshDropdownValues,
}) => {
  const [step, setStep] = useState("upload"); // upload | processing | review
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [reviewRows, setReviewRows] = useState([]);
  const [failedRows, setFailedRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [processStatus, setProcessStatus] = useState("");
  const [previewFileId, setPreviewFileId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [showOnlyFlagged, setShowOnlyFlagged] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const previewUrlCacheRef = useRef({});
  const previewFileIdRef = useRef(null);
  const lastFixNextFlaggedFileIdRef = useRef(null);

  const providerOptions = useMemo(
    () =>
      [...new Set((providers || []).map((p) => String(p).trim()).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b, undefined, { numeric: true }),
      ),
    [providers],
  );

  const reviewRowMeta = useMemo(() => {
    const byFileId = {};
    let flaggedInvoiceCount = 0;
    let flaggedFieldCount = 0;
    for (const row of reviewRows) {
      const fieldStatuses = getReviewFieldStatuses(row);
      const issueCount = Object.keys(fieldStatuses).length;
      byFileId[row.fileId] = {
        fieldStatuses,
        issueCount,
        isFlagged: issueCount > 0,
      };
      if (issueCount > 0) {
        flaggedInvoiceCount += 1;
        flaggedFieldCount += issueCount;
      }
    }
    return {
      byFileId,
      flaggedInvoiceCount,
      flaggedFieldCount,
      readyCount: reviewRows.length - flaggedInvoiceCount,
    };
  }, [reviewRows]);

  const displayedReviewRows = useMemo(() => {
    if (!showOnlyFlagged) return reviewRows;
    return reviewRows.filter((row) => reviewRowMeta.byFileId[row.fileId]?.isFlagged);
  }, [reviewRows, showOnlyFlagged, reviewRowMeta]);

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const revokePreviewUrls = () => {
    Object.values(previewUrlCacheRef.current).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
    previewUrlCacheRef.current = {};
    previewFileIdRef.current = null;
    lastFixNextFlaggedFileIdRef.current = null;
    setPreviewFileId(null);
    setPreviewUrl(null);
    setPreviewLoading(false);
    setPreviewError(null);
    setShowOnlyFlagged(false);
  };

  const closePreview = useCallback(() => {
    previewFileIdRef.current = null;
    setPreviewFileId(null);
    setPreviewLoading(false);
    setPreviewError(null);
  }, []);

  const openPreview = async (fileId) => {
    if (!sessionId || fileId == null) return;
    previewFileIdRef.current = fileId;
    lastFixNextFlaggedFileIdRef.current = fileId;
    setPreviewFileId(fileId);
    setPreviewError(null);
    const cached = previewUrlCacheRef.current[fileId];
    if (cached) {
      setPreviewUrl(cached);
      setPreviewLoading(false);
      return;
    }
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const blob = await apiService.getInvoiceUploadSessionFilePdf(
        sessionId,
        fileId,
      );
      const pdfBlob =
        blob.type === "application/pdf"
          ? blob
          : new Blob([blob], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      const existing = previewUrlCacheRef.current[fileId];
      if (existing && existing !== url) URL.revokeObjectURL(existing);
      previewUrlCacheRef.current[fileId] = url;
      if (previewFileIdRef.current !== fileId) return;
      setPreviewUrl(url);
    } catch (err) {
      if (previewFileIdRef.current !== fileId) return;
      setPreviewError(err.message || "Failed to load PDF");
      setPreviewUrl(null);
    } finally {
      if (previewFileIdRef.current === fileId) {
        setPreviewLoading(false);
      }
    }
  };

  const resetUpload = () => {
    clearPoll();
    revokePreviewUrls();
    setStep("upload");
    setSelectedFiles([]);
    setSessionId(null);
    setReviewRows([]);
    setFailedRows([]);
    setValidationErrors([]);
    setError(null);
    setBusy(false);
    setProcessStatus("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    const cache = previewUrlCacheRef;
    return () => {
      Object.values(cache.current).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      cache.current = {};
    };
  }, []);

  const handleCancel = async () => {
    clearPoll();
    if (sessionId && step !== "upload") {
      try {
        await apiService.cancelInvoiceUploadSession(sessionId);
      } catch {
        /* best-effort */
      }
    }
    resetUpload();
    if (typeof onBack === "function") onBack();
  };

  const onFilesPicked = (fileList) => {
    const pdfs = Array.from(fileList || []).filter((f) =>
      /\.pdf$/i.test(f.name || ""),
    );
    if (!pdfs.length) return;
    setSelectedFiles((prev) => {
      const key = (f) => `${f.name}::${f.size}::${f.lastModified}`;
      const seen = new Set(prev.map(key));
      const next = [...prev];
      for (const f of pdfs) {
        const k = key(f);
        if (!seen.has(k)) {
          seen.add(k);
          next.push(f);
        }
      }
      return next;
    });
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const pollUntilReady = (id) =>
    new Promise((resolve, reject) => {
      let attempts = 0;
      clearPoll();
      pollRef.current = setInterval(async () => {
        attempts += 1;
        try {
          const res = await apiService.getInvoiceUploadSession(id);
          const session = res?.data;
          const status = session?.status;
          setProcessStatus(status || "");
          if (status === "ready_for_review") {
            clearPoll();
            resolve(session);
          } else if (
            status === "cancelled" ||
            status === "expired" ||
            status === "completed"
          ) {
            clearPoll();
            reject(new Error(`Session ended with status ${status}`));
          } else if (attempts > 180) {
            clearPoll();
            reject(new Error("Processing timed out. Please try again."));
          }
        } catch (err) {
          clearPoll();
          reject(err);
        }
      }, 2000);
    });

  const handleProcess = async () => {
    if (!selectedFiles.length) return;
    setBusy(true);
    setError(null);
    try {
      const created = await apiService.createInvoiceUploadSession();
      const id = created?.data?.id;
      if (!id) throw new Error("Failed to create upload session");
      setSessionId(id);

      await apiService.uploadInvoiceSessionFiles(id, selectedFiles);
      await apiService.processInvoiceUploadSession(id);
      setStep("processing");
      setProcessStatus("processing");

      const session = await pollUntilReady(id);
      const files = (session.files || []).filter((f) => f.status !== "discarded");

      const failed = [];
      const extracted = [];

      for (const f of files) {
        const filename = f.originalFilename || "Unknown file";
        if (f.status === "failed") {
          failed.push({
            fileId: f.id,
            originalFilename: filename,
            errorMessage:
              f.errorMessage ||
              "Extraction failed. No details were returned.",
          });
          continue;
        }

        if (f.status === "extracted") {
          // Treat all-null / empty extractions as failures (no empty review row)
          if (!hasAnyExtractedField(f.extracted)) {
            failed.push({
              fileId: f.id,
              originalFilename: filename,
              errorMessage:
                f.errorMessage || EMPTY_EXTRACTION_MESSAGE,
            });
            continue;
          }
          extracted.push({
            fileId: f.id,
            originalFilename: filename,
            status: f.status,
            errorMessage: f.errorMessage,
            close_account: false,
            ...fieldsFromExtracted(
              f.extracted,
              providerOptions,
              billingAccountsByProvider,
            ),
          });
        }
      }

      setFailedRows(failed);
      setReviewRows(extracted);
      setStep("review");
      if (failed.length > 0 && extracted.length === 0) {
        setError(
          `${failed.length} invoice(s) failed extraction. See details below.`,
        );
      } else if (failed.length > 0) {
        setError(
          `${failed.length} of ${files.length} invoice(s) failed extraction. Successful ones are ready to review below.`,
        );
      }
    } catch (err) {
      setError(err.message || "Failed to process invoices");
      setStep("upload");
    } finally {
      setBusy(false);
    }
  };

  const buildValidationErrors = (rows) => {
    const invalid = [];
    for (const r of rows) {
      const issues = validateReviewRow(r);
      if (issues.length) {
        invalid.push({
          fileId: r.fileId,
          lines: issues.map((issue) => formatValidationLine(r, issue)),
        });
      }
    }
    return invalid;
  };

  const updateRow = (fileId, key, value) => {
    setReviewRows((prev) => {
      const nextRows = prev.map((r) => {
        if (r.fileId !== fileId) return r;
        if (key === "provider") {
          return {
            ...r,
            provider: value,
            billing_account: "",
            close_account: false,
            notConfiguredMessage:
              value && !r.extracted_billing_account
                ? ""
                : r.notConfiguredMessage,
          };
        }
        if (key === "billing_account") {
          return {
            ...r,
            billing_account: value,
            close_account: value ? r.close_account : false,
            notConfiguredMessage:
              r.provider && value ? "" : r.notConfiguredMessage,
          };
        }
        return { ...r, [key]: value };
      });

      setValidationErrors((prevErrs) => {
        if (!prevErrs.length) return prevErrs;
        const invalid = buildValidationErrors(nextRows);
        if (invalid.length === 0) {
          setError((prev) =>
            prev && String(prev).includes("missing required fields")
              ? null
              : prev,
          );
          return [];
        }
        setError(
          `${invalid.length} invoice(s) are missing required fields. Fix the issues below before saving.`,
        );
        return invalid;
      });

      return nextRows;
    });
  };

  const accountsForRow = (provider) =>
    (billingAccountsByProvider || [])
      .filter((account) => account.provider === provider && account.billing_account)
      .sort((a, b) =>
        String(a.billing_account).localeCompare(String(b.billing_account), undefined, {
          numeric: true,
        }),
      );

  const fixNextFlaggedRow = () => {
    const flagged = reviewRows.filter(
      (row) => reviewRowMeta.byFileId[row.fileId]?.isFlagged,
    );
    if (!flagged.length) return;

    const activeFileId =
      previewFileIdRef.current ?? lastFixNextFlaggedFileIdRef.current;
    let currentIdx = -1;
    if (activeFileId != null) {
      currentIdx = flagged.findIndex((row) => row.fileId === activeFileId);
    }
    const nextIdx =
      currentIdx >= 0 ? (currentIdx + 1) % flagged.length : 0;
    openPreview(flagged[nextIdx].fileId);
  };

  const notifyPreviewSaved = (stillFlagged) => {
    if (typeof setToast !== "function") return;
    setToast({
      type: stillFlagged ? "warning" : "success",
      message: stillFlagged
        ? "Invoice saved to the review table. Some required fields are still missing."
        : "Invoice saved successfully. All fields are ready in the review table.",
    });
  };

  const savePreviewAndClose = () => {
    const stillFlagged = Boolean(
      reviewRowMeta.byFileId[previewFileId]?.isFlagged,
    );
    notifyPreviewSaved(stillFlagged);
    closePreview();
  };

  const savePreviewAndNext = () => {
    const stillFlagged = Boolean(
      reviewRowMeta.byFileId[previewFileId]?.isFlagged,
    );
    notifyPreviewSaved(stillFlagged);
    const flagged = reviewRows.filter(
      (row) => reviewRowMeta.byFileId[row.fileId]?.isFlagged,
    );
    if (flagged.length <= 1) {
      closePreview();
      return;
    }
    const currentIdx = flagged.findIndex((row) => row.fileId === previewFileId);
    const nextIdx =
      currentIdx >= 0 ? (currentIdx + 1) % flagged.length : 0;
    const nextFileId = flagged[nextIdx].fileId;
    if (nextFileId === previewFileId) {
      closePreview();
      return;
    }
    openPreview(nextFileId);
  };

  const removeRow = async (fileId) => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      if (previewFileId === fileId) closePreview();
      const cached = previewUrlCacheRef.current[fileId];
      if (cached) {
        URL.revokeObjectURL(cached);
        delete previewUrlCacheRef.current[fileId];
      }
      await apiService.discardInvoiceUploadSessionFile(sessionId, fileId);
      setReviewRows((prev) => {
        const nextRows = prev.filter((r) => r.fileId !== fileId);
        setValidationErrors((prevErrs) => {
          if (!prevErrs.length) return prevErrs;
          const invalid = buildValidationErrors(nextRows);
          if (invalid.length === 0) return [];
          setError(
            `${invalid.length} invoice(s) are missing required fields. Fix the issues below before saving.`,
          );
          return invalid;
        });
        return nextRows;
      });
      setFailedRows((prev) => prev.filter((r) => r.fileId !== fileId));
    } catch (err) {
      setError(err.message || "Failed to remove invoice");
    } finally {
      setBusy(false);
    }
  };

  const dismissFailedRow = async (fileId) => {
    await removeRow(fileId);
  };

  const closeAccountsForRows = async (rows) => {
    if (!canCloseAccount || !Array.isArray(rows) || rows.length === 0) {
      return { closedCount: 0, failedCount: 0 };
    }
    const unique = new Map();
    for (const row of rows) {
      if (!row?.close_account) continue;
      const provider = String(row.provider || "").trim();
      const account = normalizeBillingAccount(row.billing_account);
      if (!provider || !account) continue;
      unique.set(`${provider}\0${account}`, { provider, account });
    }
    let closedCount = 0;
    let failedCount = 0;
    for (const { provider, account } of unique.values()) {
      try {
        await apiService.closeInvoiceBillingAccount(provider, account);
        closedCount += 1;
      } catch {
        failedCount += 1;
      }
    }
    return { closedCount, failedCount };
  };

  const handleSave = async () => {
    if (!sessionId || !reviewRows.length) return;

    const invalid = buildValidationErrors(reviewRows);

    if (invalid.length) {
      setValidationErrors(invalid);
      setError(
        `${invalid.length} invoice(s) are missing required fields. Fix the issues below before saving.`,
      );
      return;
    }

    setValidationErrors([]);
    setBusy(true);
    setError(null);
    try {
      const payload = reviewRows.map((r) => ({
        fileId: r.fileId,
        provider: r.provider,
        invoice_number: r.invoice_number,
        invoice_date: r.invoice_date,
        billing_account: r.billing_account,
        gl_code: r.gl_code,
        monthly_mrc: r.monthly_mrc,
        taxes_and_surcharges: r.taxes_and_surcharges || null,
        total_invoice_amount: r.total_invoice_amount,
        due_date: r.due_date || null,
      }));
      const res = await apiService.confirmInvoiceUploadSession(sessionId, {
        rows: payload,
      });
      const savedCount = res?.data?.saved?.length || 0;
      const saveErrors = res?.data?.errors || [];
      const errCount = saveErrors.length;
      const savedIds = new Set(
        (res?.data?.saved || []).map((s) => s.fileId ?? s.file_id),
      );
      const rowsToClose = reviewRows.filter((r) => savedIds.has(r.fileId));
      const { closedCount, failedCount: closeFailedCount } =
        await closeAccountsForRows(rowsToClose);

      if (closedCount > 0 || closeFailedCount > 0) {
        if (typeof refreshDropdownValues === "function") {
          refreshDropdownValues();
        }
      }

      const closeSuffix =
        closedCount > 0
          ? closeFailedCount > 0
            ? ` Closed ${closedCount} account(s); ${closeFailedCount} close failed.`
            : ` Closed ${closedCount} account(s) as last invoice.`
          : closeFailedCount > 0
            ? ` ${closeFailedCount} account close(s) failed.`
            : "";

      if (errCount > 0) {
        const detailRows = saveErrors.map((e) => {
          const row = reviewRows.find((r) => r.fileId === e.fileId);
          const issues = String(e.message || "Save failed")
            .split(";")
            .map((s) => s.trim())
            .filter(Boolean);
          const lines = row
            ? issues.map((issue) => formatValidationLine(row, issue))
            : issues;
          return {
            fileId: e.fileId,
            lines,
          };
        });
        setValidationErrors(detailRows);
        setError(
          savedCount > 0
            ? `Saved ${savedCount} invoice(s). ${errCount} could not be saved — see details below.`
            : `${errCount} invoice(s) could not be saved — see details below.`,
        );
        if (savedCount > 0) {
          setReviewRows((prev) => prev.filter((r) => !savedIds.has(r.fileId)));
        }
        if (typeof setToast === "function") {
          setToast({
            message:
              savedCount > 0
                ? `${savedCount} invoice(s) saved; ${errCount} still need attention.${closeSuffix}`
                : `Could not save ${errCount} invoice(s). See error details below.`,
            type: "warning",
          });
        }
        return;
      }

      if (typeof setToast === "function") {
        setToast({
          message: `${savedCount} invoice(s) saved successfully.${closeSuffix}`,
          type: closeFailedCount > 0 ? "warning" : "success",
        });
      }
      resetUpload();
      if (typeof onSaved === "function") onSaved(res);
    } catch (err) {
      const saveErrors = Array.isArray(err.details?.details)
        ? err.details.details
        : Array.isArray(err.details)
          ? err.details
          : [];
      if (saveErrors.length > 0) {
        const detailRows = saveErrors.map((e) => {
          const row = reviewRows.find((r) => r.fileId === e.fileId);
          const issues = String(e.message || "Save failed")
            .split(";")
            .map((s) => s.trim())
            .filter(Boolean);
          const lines = row
            ? issues.map((issue) => formatValidationLine(row, issue))
            : issues;
          return {
            fileId: e.fileId,
            lines,
          };
        });
        setValidationErrors(detailRows);
        setError(
          err.message ||
            `${saveErrors.length} invoice(s) could not be saved — see details below.`,
        );
      } else {
        setError(err.message || "Failed to save invoices");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {error && (
        <div className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {step === "upload" && (
        <>
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-stone-500">
              Documents
            </p>
            <div className="group relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-stone-200 bg-stone-50/50 px-6 py-10 transition-colors hover:border-stone-300 hover:bg-stone-50">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                multiple
                className="absolute inset-0 z-10 h-full w-full cursor-pointer rounded-xl opacity-0"
                onChange={(e) => onFilesPicked(e.target.files)}
              />
              <UploadCloudIcon className="mb-3 h-10 w-10 text-stone-400 transition-colors group-hover:text-stone-500" />
              <p className="text-center text-base font-semibold text-stone-900">
                Click or drag multiple invoice PDFs here
              </p>
              <p className="mt-1 text-center text-sm text-stone-500">
                Each invoice is processed independently
              </p>
            </div>

            {selectedFiles.length > 0 && (
              <ul className="mt-4 space-y-2">
                {selectedFiles.map((f, index) => (
                  <li
                    key={`${f.name}-${f.size}-${f.lastModified}-${index}`}
                    className="flex items-center gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2.5"
                  >
                    <FileText className="h-5 w-5 shrink-0 text-stone-400" />
                    <span className="min-w-0 flex-1 truncate text-sm text-stone-900">
                      {f.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSelectedFile(index)}
                      disabled={busy}
                      className="shrink-0 rounded-md p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700 disabled:opacity-50"
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-lg border border-stone-300 bg-white px-4 py-2.5 font-medium text-stone-700 transition-colors hover:bg-stone-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleProcess}
              disabled={!selectedFiles.length || busy}
              className="inline-flex items-center justify-center rounded-lg border border-transparent bg-[var(--accent,#a51c1c)] px-5 py-2.5 text-base font-medium text-white shadow-sm hover:bg-[#8c1a1a] disabled:cursor-not-allowed disabled:bg-stone-300"
            >
              {busy ? "Uploading…" : "Process invoice(s)"}
            </button>
          </div>
        </>
      )}

      {step === "processing" && (
        <div className="py-16 text-center">
          <p className="text-base font-medium text-stone-900">
            Processing invoices…
          </p>
          <p className="mt-2 text-sm text-stone-500">
            Each PDF is sent to Claude individually. Status:{" "}
            {processStatus || "starting"}
          </p>
        </div>
      )}

      {step === "review" && (
        <>
          {failedRows.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <h3 className="text-sm font-semibold text-red-900">
                Failed invoices ({failedRows.length})
              </h3>
              <p className="mt-1 text-sm text-red-800">
                These PDFs could not be extracted. They will not be saved unless
                you re-upload them.
              </p>
              <ul className="mt-3 space-y-2">
                {failedRows.map((row) => (
                  <li
                    key={row.fileId}
                    className="flex flex-col gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 sm:flex-row sm:items-start sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="break-all text-sm font-medium text-stone-900">
                        {row.originalFilename}
                      </p>
                      <p className="mt-0.5 text-sm text-red-700">
                        Reason: {row.errorMessage}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openPreview(row.fileId)}
                        disabled={busy}
                        className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-100 disabled:opacity-50"
                      >
                        <FileText className="h-4 w-4" />
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => dismissFailedRow(row.fileId)}
                        disabled={busy}
                        className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" />
                        Dismiss
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {validationErrors.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <h3 className="text-sm font-semibold text-red-900">
                Cannot save ({validationErrors.length})
              </h3>
              <p className="mt-1 text-sm text-red-800">
                {validationErrors.some((row) =>
                  (row.lines || []).some((line) =>
                    String(line).toLowerCase().includes("already exists"),
                  ),
                )
                  ? "One or more invoices are duplicates or could not be saved. Fix the issues below, then try Save again."
                  : "Required fields: Invoice Number, Invoice Date, Billing Account, MRC, and Total Invoice Amount. Fix the issues below, then try Save again."}
              </p>
              <ul className="mt-3 space-y-2">
                {validationErrors.map((row) => (
                  <li
                    key={row.fileId}
                    className="space-y-1 rounded-lg border border-red-200 bg-white px-3 py-2"
                  >
                    {(row.lines || []).map((line) => (
                      <p
                        key={line}
                        className="break-words text-sm text-red-800"
                      >
                        ⚠️ {line}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="text-base font-semibold text-stone-900">
              Review Extracted Invoices
            </h3>
            <p className="mt-1 text-sm text-stone-600">
              {reviewRows.length > 0
                ? "Review the extracted invoice information below. Click any cell to edit its value, or open the preview to check the source PDF side by side."
                : "No invoices were extracted successfully. Fix or re-upload the failed files above."}
            </p>
          </div>

          {reviewRows.length > 0 ? (
          <>
          {reviewRowMeta.flaggedInvoiceCount > 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
                    <AlertCircle className="h-4.5 w-4.5 text-amber-600" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-amber-950">
                      {reviewRowMeta.flaggedInvoiceCount} invoice
                      {reviewRowMeta.flaggedInvoiceCount === 1 ? "" : "s"} need attention
                    </p>
                    <p className="mt-1 text-sm text-amber-800">
                      Open the preview on a flagged row to read the value off the invoice and fill it in.
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setShowOnlyFlagged((prev) => !prev)}
                    className="rounded-lg border border-[var(--line-strong,#d5d3cc)] bg-white px-3 py-1.5 text-sm font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                  >
                    {showOnlyFlagged ? "Show all rows" : "Show only flagged"}
                  </button>
                  <button
                    type="button"
                    onClick={fixNextFlaggedRow}
                    disabled={busy}
                    className="rounded-lg border border-transparent bg-[var(--accent,#a51c1c)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#8c1a1a] disabled:opacity-50"
                  >
                    Fix next flagged row
                  </button>
                </div>
              </div>
            </div>
          ) : null}

<div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
  <table className="w-full min-w-[1750px] border-collapse text-sm">
    <thead className="border-b border-gray-300 bg-white text-left">
      <tr>
        <th className="min-w-[90px] whitespace-nowrap px-4 py-4 text-[12px] font-semibold uppercase tracking-wide text-gray-600">
          Preview
        </th>

        <th className="min-w-[160px] whitespace-nowrap px-4 py-4 text-[12px] font-semibold uppercase tracking-wide text-gray-600">
          Provider
          <span className="ml-0.5 text-red-500">*</span>
        </th>

        <th className="min-w-[180px] whitespace-nowrap px-4 py-4 text-[12px] font-semibold uppercase tracking-wide text-gray-600">
          Invoice Number
          <span className="ml-0.5 text-red-500">*</span>
        </th>

        <th className="min-w-[160px] whitespace-nowrap px-4 py-4 text-[12px] font-semibold uppercase tracking-wide text-gray-600">
          Invoice Date
          <span className="ml-0.5 text-red-500">*</span>
        </th>

        <th className="min-w-[210px] whitespace-nowrap px-4 py-4 text-[12px] font-semibold uppercase tracking-wide text-gray-600">
          Billing Account
          <span className="ml-0.5 text-red-500">*</span>
        </th>

        <th className="min-w-[200px] whitespace-nowrap px-4 py-4 text-[12px] font-semibold uppercase tracking-wide text-gray-600">
          GL Code
          <span className="ml-0.5 text-red-500">*</span>
        </th>

        <th className="min-w-[140px] whitespace-nowrap px-4 py-4 text-[12px] font-semibold uppercase tracking-wide text-gray-600">
          MRC
          <span className="ml-0.5 text-red-500">*</span>
        </th>

        <th className="min-w-[170px] whitespace-nowrap px-4 py-4 text-[12px] font-semibold uppercase tracking-wide text-gray-600">
          Taxes and Surcharges
        </th>

        <th className="min-w-[190px] whitespace-nowrap px-4 py-4 text-[12px] font-semibold uppercase tracking-wide text-gray-600">
          Total Invoice Amount
          <span className="ml-0.5 text-red-500">*</span>
        </th>

        <th className="min-w-[160px] whitespace-nowrap px-4 py-4 text-[12px] font-semibold uppercase tracking-wide text-gray-600">
          Due Date
        </th>

        <th className="min-w-[150px] whitespace-nowrap px-4 py-4 text-[12px] font-semibold uppercase tracking-wide text-gray-600">
          Action
        </th>
      </tr>
    </thead>

    <tbody>
      {displayedReviewRows.map((row) => {
        const meta = reviewRowMeta.byFileId[row.fileId] || {
          fieldStatuses: {},
          issueCount: 0,
          isFlagged: false,
        };

        const { fieldStatuses, issueCount, isFlagged } = meta;

        return (
          <React.Fragment key={row.fileId}>
            <tr
              className={`align-middle border-b border-gray-200 transition-colors ${
                isFlagged
                  ? "border-l-4 border-l-red-500 bg-white"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              {/* =========================
                  PREVIEW
              ========================== */}
              <td className="min-w-[90px] px-4 py-4 align-middle">
                <button
                  type="button"
                  onClick={() => openPreview(row.fileId)}
                  disabled={busy}
                  className={`group/pdf relative inline-flex items-center justify-center rounded-lg p-1.5 transition-all disabled:opacity-50 ${
                    isFlagged
                      ? "hover:bg-red-50"
                      : "hover:bg-gray-100"
                  }`}
                  aria-label={`Preview ${
                    row.originalFilename || "invoice PDF"
                  }`}
                  title={
                    issueCount > 0
                      ? `${issueCount} field${
                          issueCount === 1 ? "" : "s"
                        } need attention`
                      : row.originalFilename || "Preview PDF"
                  }
                >
                  <PdfFileIcon className="h-14 w-14 drop-shadow-sm transition-transform group-hover/pdf:-translate-y-0.5" />

                  {issueCount > 0 ? (
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.125rem] items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[10px] font-bold leading-none text-white shadow-sm">
                      {issueCount}
                    </span>
                  ) : null}
                </button>
              </td>

              {/* =========================
                  PROVIDER
              ========================== */}
              <td className="min-w-[160px] px-4 py-4 align-middle">
                <div className="relative w-full">
                  <CustomSelect
                    className={`${reviewFieldClass(
                      fieldStatuses.provider,
                    )} h-11 w-full rounded-lg`}
                    value={
                      providerOptions.includes(row.provider)
                        ? row.provider
                        : ""
                    }
                    onChange={(e) => {
                      updateRow(
                        row.fileId,
                        "provider",
                        e.target.value,
                      );
                    }}
                    disabled={busy}
                  >
                    <option value="">Select provider</option>

                    {providerOptions.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </CustomSelect>

                  <ReviewFieldHint
                    status={fieldStatuses.provider}
                  />
                </div>
              </td>

              {/* =========================
                  INVOICE NUMBER
              ========================== */}
              <td className="min-w-[180px] px-4 py-4 align-middle">
                <div className="relative w-full">
                  <input
                    className={`${reviewFieldClass(
                      fieldStatuses.invoice_number,
                    )} h-11 w-full rounded-lg`}
                    value={row.invoice_number}
                    onChange={(e) =>
                      updateRow(
                        row.fileId,
                        "invoice_number",
                        e.target.value,
                      )
                    }
                    disabled={busy}
                  />

                  <ReviewFieldHint
                    status={fieldStatuses.invoice_number}
                  />
                </div>
              </td>

              {/* =========================
                  INVOICE DATE
              ========================== */}
              <td className="min-w-[160px] px-4 py-4 align-middle">
                <div
                  className="relative w-full"
                  title={fieldStatuses.invoice_date?.message}
                >
                  <YmdDateInput
                    className={`${reviewFieldClass(
                      fieldStatuses.invoice_date,
                    )} h-11 w-full rounded-lg`}
                    value={row.invoice_date}
                    onChange={(e) =>
                      updateRow(
                        row.fileId,
                        "invoice_date",
                        e.target.value,
                      )
                    }
                    max={getCurrentInvoiceMonthEndDate()}
                    disabled={busy}
                  />

                  <ReviewFieldHint
                    status={fieldStatuses.invoice_date}
                  />
                </div>
              </td>

              {/* =========================
                  BILLING ACCOUNT
              ========================== */}
              <td className="min-w-[210px] px-4 py-4 align-middle">
                {(() => {
                  const accounts = accountsForRow(row.provider);

                  const inList = accounts.some(
                    (account) =>
                      normalizeBillingAccount(
                        account.billing_account,
                      ) ===
                      normalizeBillingAccount(
                        row.billing_account,
                      ),
                  );

                  return (
                    <div className="relative w-full">
                      <CustomSelect
                        className={`${reviewFieldClass(
                          fieldStatuses.billing_account,
                        )} h-11 w-full rounded-lg`}
                        value={
                          inList
                            ? normalizeBillingAccount(
                                row.billing_account,
                              )
                            : ""
                        }
                        onChange={(e) =>
                          updateRow(
                            row.fileId,
                            "billing_account",
                            e.target.value,
                          )
                        }
                        disabled={busy || !row.provider}
                      >
                        <option value="">
                          {row.provider
                            ? "Select account"
                            : "Select provider first"}
                        </option>

                        {accounts.map((account) => (
                          <option
                            key={`${account.provider}-${account.billing_account}`}
                            value={normalizeBillingAccount(
                              account.billing_account,
                            )}
                          >
                            {account.show_as ||
                              account.billing_account}
                          </option>
                        ))}
                      </CustomSelect>

                      <ReviewFieldHint
                        status={fieldStatuses.billing_account}
                      />
                    </div>
                  );
                })()}
              </td>

              {/* =========================
                  GL CODE
              ========================== */}
              <td className="min-w-[200px] px-4 py-4 align-middle">
                <div className="relative w-full">
                  <CustomSelect
                    className={`${reviewFieldClass(
                      fieldStatuses.gl_code,
                    )} h-11 w-full rounded-lg`}
                    value={
                      glCodesForDropdown(
                        glCodes,
                        row.gl_code,
                      ).some(
                        (code) =>
                          String(code.gl_code) ===
                          String(row.gl_code || ""),
                      )
                        ? String(row.gl_code || "")
                        : ""
                    }
                    onChange={(e) =>
                      updateRow(
                        row.fileId,
                        "gl_code",
                        e.target.value,
                      )
                    }
                    disabled={busy}
                  >
                    <option value="">Select GL code</option>

                    {glCodesForDropdown(
                      glCodes,
                      row.gl_code,
                    ).map((code) => (
                      <option
                        key={code.gl_code}
                        value={code.gl_code}
                      >
                        {formatGlCodeLabel(
                          code.gl_code,
                          code.description,
                        )}
                      </option>
                    ))}
                  </CustomSelect>

                  <ReviewFieldHint
                    status={fieldStatuses.gl_code}
                  />
                </div>
              </td>

              {/* =========================
                  MRC
              ========================== */}
              <td className="min-w-[140px] px-4 py-4 align-middle">
                <div className="relative w-full">
                  <input
                    type="number"
                    step="0.01"
                    className={`${reviewFieldClass(
                      fieldStatuses.monthly_mrc,
                    )} h-11 w-full rounded-lg [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
                    value={row.monthly_mrc}
                    placeholder={
                      fieldStatuses.monthly_mrc
                        ? "Missing"
                        : undefined
                    }
                    onChange={(e) =>
                      updateRow(
                        row.fileId,
                        "monthly_mrc",
                        e.target.value,
                      )
                    }
                    onWheel={(e) => e.target.blur()}
                    disabled={busy}
                  />

                  <ReviewFieldHint
                    status={fieldStatuses.monthly_mrc}
                  />
                </div>
              </td>

              {/* =========================
                  TAXES AND SURCHARGES
              ========================== */}
              <td className="min-w-[170px] px-4 py-4 align-middle">
                <div className="relative w-full">
                  <input
                    type="number"
                    step="0.01"
                    className={`${reviewFieldClass(
                      fieldStatuses.taxes_and_surcharges,
                    )} h-11 w-full rounded-lg [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
                    value={row.taxes_and_surcharges}
                    placeholder={
                      fieldStatuses.taxes_and_surcharges
                        ? "Missing"
                        : undefined
                    }
                    onChange={(e) =>
                      updateRow(
                        row.fileId,
                        "taxes_and_surcharges",
                        e.target.value,
                      )
                    }
                    onWheel={(e) => e.target.blur()}
                    disabled={busy}
                  />

                  <ReviewFieldHint
                    status={
                      fieldStatuses.taxes_and_surcharges
                    }
                  />
                </div>
              </td>

              {/* =========================
                  TOTAL INVOICE AMOUNT
              ========================== */}
              <td className="min-w-[190px] px-4 py-4 align-middle">
                <div
                  className="relative w-full"
                  title={
                    fieldStatuses.total_invoice_amount
                      ?.message
                  }
                >
                  <input
                    type="number"
                    step="0.01"
                    className={`${reviewFieldClass(
                      fieldStatuses.total_invoice_amount,
                    )} h-11 w-full rounded-lg [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
                    value={row.total_invoice_amount}
                    placeholder={
                      fieldStatuses.total_invoice_amount
                        ?.level === "required"
                        ? "Missing"
                        : undefined
                    }
                    onChange={(e) =>
                      updateRow(
                        row.fileId,
                        "total_invoice_amount",
                        e.target.value,
                      )
                    }
                    onWheel={(e) => e.target.blur()}
                    disabled={busy}
                  />

                  <ReviewFieldHint
                    status={
                      fieldStatuses.total_invoice_amount
                    }
                  />
                </div>
              </td>

              {/* =========================
                  DUE DATE
              ========================== */}
              <td className="min-w-[160px] px-4 py-4 align-middle">
                <div
                  className="relative w-full"
                  title={fieldStatuses.due_date?.message}
                >
                  <YmdDateInput
                    className={`${reviewFieldClass(
                      fieldStatuses.due_date,
                    )} h-11 w-full rounded-lg`}
                    value={row.due_date}
                    onChange={(e) =>
                      updateRow(
                        row.fileId,
                        "due_date",
                        e.target.value,
                      )
                    }
                    disabled={busy}
                  />

                  <ReviewFieldHint
                    status={fieldStatuses.due_date}
                  />
                </div>
              </td>

              {/* =========================
                  ACTION
              ========================== */}
              <td className="min-w-[150px] px-4 py-4 align-middle">
                <div className="flex flex-col items-start gap-2">
                  {canCloseAccount ? (
                    <label
                      className={`inline-flex items-center gap-2 text-sm text-stone-700 ${
                        busy || !row.provider || !row.billing_account
                          ? "opacity-50"
                          : "cursor-pointer"
                      }`}
                      title="After save, close this billing account (last invoice)"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-stone-300 text-stone-800 focus:ring-stone-500"
                        checked={Boolean(row.close_account)}
                        disabled={
                          busy || !row.provider || !row.billing_account
                        }
                        onChange={(e) =>
                          updateRow(
                            row.fileId,
                            "close_account",
                            e.target.checked,
                          )
                        }
                      />
                      <span className="whitespace-nowrap">Close account</span>
                    </label>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => removeRow(row.fileId)}
                    disabled={busy}
                    className="inline-flex h-11 items-center gap-1 rounded-lg px-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </td>
            </tr>

            {/* =========================
                NOT CONFIGURED MESSAGE
            ========================== */}
            {row.notConfiguredMessage ? (
              <tr className="bg-amber-50/70">
                <td
                  colSpan={11}
                  className="px-4 py-3 text-sm text-amber-800"
                >
                  {row.notConfiguredMessage}
                </td>
              </tr>
            ) : null}
          </React.Fragment>
        );
      })}
    </tbody>
  </table>
</div>

            {showOnlyFlagged && displayedReviewRows.length === 0 ? (
              <p className="text-sm text-stone-600">
                No flagged invoices remain.{" "}
                <button
                  type="button"
                  onClick={() => setShowOnlyFlagged(false)}
                  className="font-medium text-stone-900 underline underline-offset-2 hover:text-stone-700"
                >
                  Show all rows
                </button>
              </p>
            ) : null}
          </>
          ) : null}

          <div className="flex items-center justify-between rounded-lg bg-stone-50 px-4 py-2.5 text-sm font-medium text-stone-700">
            {reviewRows.length > 0 ? (
              <span>
                {reviewRowMeta.readyCount} invoice
                {reviewRowMeta.readyCount === 1 ? "" : "s"} ready to save
                {reviewRowMeta.flaggedFieldCount > 0
                  ? ` · ${reviewRowMeta.flaggedFieldCount} still flagged`
                  : ""}
                {failedRows.length > 0 ? ` · Failed: ${failedRows.length}` : ""}
                {canCloseAccount &&
                reviewRows.some((r) => r.close_account)
                  ? ` · ${
                      reviewRows.filter((r) => r.close_account).length
                    } marked to close`
                  : ""}
              </span>
            ) : (
              <span>
                Total ready to save: 0
                {failedRows.length > 0 ? ` · Failed: ${failedRows.length}` : ""}
              </span>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              disabled={busy}
              className="rounded-lg border border-stone-300 bg-white px-4 py-2.5 font-medium text-stone-700 transition-colors hover:bg-stone-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || reviewRows.length === 0}
              className="inline-flex items-center justify-center rounded-lg border border-transparent bg-[var(--accent,#a51c1c)] px-5 py-2.5 text-base font-medium text-white shadow-sm hover:bg-[#8c1a1a] disabled:cursor-not-allowed disabled:bg-stone-300"
            >
              {busy
                ? "Saving…"
                : reviewRowMeta.flaggedFieldCount > 0
                  ? `Save Invoices (${reviewRowMeta.flaggedFieldCount} flagged)`
                  : "Save"}
            </button>
          </div>
        </>
      )}

      {previewFileId != null ? (
        <InvoiceExtractionPdfPreviewModal
          row={
            reviewRows.find((r) => r.fileId === previewFileId) ||
            failedRows.find((r) => r.fileId === previewFileId) ||
            null
          }
          pdfUrl={previewUrl}
          loading={previewLoading}
          error={previewError}
          busy={busy}
          providerOptions={providerOptions}
          glCodes={glCodes}
          accountsForRow={accountsForRow}
          onUpdate={updateRow}
          onClose={closePreview}
          onSaveAndClose={savePreviewAndClose}
          onSaveAndNext={savePreviewAndNext}
          hasNextFlagged={reviewRows.some(
            (row) =>
              row.fileId !== previewFileId &&
              reviewRowMeta.byFileId[row.fileId]?.isFlagged,
          )}
        />
      ) : null}
    </div>
  );
};

export default InvoiceExtractionUpload;